// src/pages/Students.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDataContext } from '../contexts/DataContext';
import { getCTECode } from '../utils/completion';

function Students() {
  const {
    students,
    programs,
    gradYears,
    currentSchoolYear,
    loading
  } = useDataContext();

  const [selectedProgram, setSelectedProgram] = useState('all');
  const [selectedGradYear, setSelectedGradYear] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [studentDetails, setStudentDetails] = useState(null);

  // If navigated from dashboard with ?program=ID, honor it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const programId = params.get('program');
    if (programId) setSelectedProgram(programId);
  }, []);

  const filteredStudents = useMemo(() => {
    let out = students;
    if (selectedProgram !== 'all') {
      out = out.filter(s => s.programStatus[selectedProgram]?.isCompleter);
    }
    if (selectedGradYear !== 'all') {
      out = out.filter(s => s.gradYear === selectedGradYear.toString());
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      out = out.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.id?.toString().includes(q)
      );
    }
    return out;
  }, [students, selectedProgram, selectedGradYear, searchQuery]);

  if (loading) return <div>Loading student data...</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Student Analysis</h1>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block mb-1 font-semibold">Filter by Program:</label>
          <select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="all">All Programs</option>
            {programs.map(program => (
              <option key={program.id} value={program.id}>
                {program.name} Completers
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1 font-semibold">Filter by Graduation Year:</label>
          <select
            value={selectedGradYear}
            onChange={(e) => setSelectedGradYear(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="all">All Graduation Years</option>
            {gradYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1 font-semibold">Search Student:</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or ID..."
            className="w-full border rounded px-3 py-2"
          />
        </div>
      </div>

      <div className="mb-4">
        <p className="text-gray-700">
          Showing {filteredStudents.length} of {students.length} students
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-4 py-2 text-left">Student ID</th>
              <th className="border px-4 py-2 text-left">Name</th>
              <th className="border px-4 py-2 text-left">Grad Year</th>
              <th className="border px-4 py-2 text-left">Programs Completed</th>
              <th className="border px-4 py-2 text-left">Potential Completions</th>
              <th className="border px-4 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map(student => {
              const completedPrograms = programs.filter(program =>
                student.programStatus[program.id]?.isCompleter
              );
              const potentialPrograms = programs.filter(program =>
                !student.programStatus[program.id]?.isCompleter &&
                student.programStatus[program.id]?.willBeCompleter
              );
              return (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-2">{student.id}</td>
                  <td className="border px-4 py-2">{student.name}</td>
                  <td className="border px-4 py-2">{student.gradYear || 'Unknown'}</td>
                  <td className="border px-4 py-2">
                    {completedPrograms.length > 0 ? (
                      <ul className="list-disc list-inside">
                        {completedPrograms.map(program => (
                          <li key={program.id}>{program.name}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </td>
                  <td className="border px-4 py-2">
                    {potentialPrograms.length > 0 ? (
                      <ul className="list-disc list-inside">
                        {potentialPrograms.map(program => (
                          <li key={program.id}>{program.name} (Projected)</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </td>
                  <td className="border px-4 py-2 text-center">
                    <button
                      onClick={() => setStudentDetails(student)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {studentDetails && (
        <StudentDetailsModal
          student={studentDetails}
          programs={programs}
          currentSchoolYear={currentSchoolYear}
          onClose={() => setStudentDetails(null)}
        />
      )}
    </div>
  );
}

export function StudentDetailsModal({ student, programs, currentSchoolYear, onClose }) {
  const [showAllPrograms, setShowAllPrograms] = useState(false);

  // Default to programs where the student has taken at least one course.
  // Keeps the modal short for students who touch just a couple of programs.
  const visiblePrograms = useMemo(() => {
    if (showAllPrograms) return programs;
    return programs.filter(p =>
      (student.programStatus[p.id]?.coursesTaken?.length || 0) > 0
    );
  }, [programs, student, showAllPrograms]);

  const hiddenCount = programs.length - visiblePrograms.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Student: {student.name} (ID: {student.id})
            {student.gradYear && (
              <span className="ml-2 text-gray-600">Grad Year: {student.gradYear}</span>
            )}
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-800">
            ✕
          </button>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Program Completion Status</h3>
          {hiddenCount > 0 && !showAllPrograms && (
            <button
              onClick={() => setShowAllPrograms(true)}
              className="text-sm text-blue-600 hover:underline"
            >
              Show {hiddenCount} program{hiddenCount === 1 ? '' : 's'} with no courses
            </button>
          )}
          {showAllPrograms && (
            <button
              onClick={() => setShowAllPrograms(false)}
              className="text-sm text-blue-600 hover:underline"
            >
              Hide programs with no courses
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {visiblePrograms.length === 0 ? (
            <div className="col-span-full text-gray-500 p-4 border rounded">
              This student has no CTE courses on record.
            </div>
          ) : (
            visiblePrograms.map(program => {
              const status = student.programStatus[program.id];
              const { code, colorClass } = getCTECode(status);

              return (
                <div key={program.id} className="border rounded p-4">
                  <h4 className="font-semibold">{program.name}</h4>

                  <div className="mt-2 flex items-center">
                    <p className="text-sm">CTE Status:</p>
                    <p className={`font-semibold ${colorClass} ml-2`}>{code}</p>
                  </div>

                  {!status?.isNotCTE ? (
                    <>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-sm">Completion Status:</p>
                          <p className={status?.isCompleter ? 'font-semibold text-green-600' : 'font-semibold text-red-600'}>
                            {status?.isCompleter ? 'Completer' : 'Not Completed'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm">Projected Status:</p>
                          <p className={status?.willBeCompleter && !status?.isCompleter ? 'font-semibold text-blue-600' : 'font-semibold text-gray-600'}>
                            {status?.willBeCompleter && !status?.isCompleter
                              ? 'Will Complete'
                              : status?.isCompleter ? 'Already Complete' : 'Will Not Complete'}
                          </p>
                        </div>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div><p className="text-sm">Courses: {status?.courseCount}/3</p></div>
                        <div>
                          <p className="text-sm">Credits: {status?.totalCredits.toFixed(1)}/4.0</p>
                          {status?.projectedTotalCredits > status?.totalCredits && (
                            <p className="text-xs text-blue-600">
                              Projected: {status.projectedTotalCredits.toFixed(1)}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-2">
                        <p className="text-sm">
                          Upper Level Course: {status?.hasUpperLevelCourse ? 'Yes' : 'No'}
                          {!status?.hasUpperLevelCourse && status?.willHaveUpperLevelCourse &&
                            ` (Projected: Yes)`}
                        </p>
                      </div>

                      {!status?.isCompleter && status?.gaps && status.gaps.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-semibold text-red-600">Completion Gaps:</p>
                          <ul className="text-sm list-disc list-inside">
                            {status.gaps.map((gap, i) => <li key={i}>{gap}</li>)}
                          </ul>
                        </div>
                      )}

                      <div className="mt-3">
                        <p className="text-sm font-semibold">Courses Taken:</p>
                        {status?.coursesTaken?.length > 0 ? (
                          <ul className="text-sm">
                            {status.coursesTaken.map((course, i) => {
                              const isUpperLevel = program.upperLevelCourses?.includes(course.courseName);
                              const isInProgress = course.year === currentSchoolYear;
                              return (
                                <li key={i} className="py-1">
                                  <span className={isUpperLevel ? 'font-semibold' : ''}>
                                    {course.courseName}
                                  </span>
                                  <span className="text-gray-600">
                                    {' '}({course.year}, {course.earnedCredits} credits
                                    {isInProgress ? ', In Progress' : ''}
                                    {isUpperLevel ? ', Upper Level' : ''})
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">No program courses taken</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="mt-3">
                      <p className="text-sm text-gray-500">No courses taken in this program</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default Students;
